/**
 * MathUtils — small math helpers used across gameplay code.
 *
 * Phase H10 Arc α (2026-05-08): replaces ad-hoc dependence on
 * `Phaser.Math.*` so the gameplay layer doesn't reach into the
 * renderer for trig/geometry helpers.
 *
 * Kept narrow on purpose — only what current call sites need. Add
 * here when a second consumer arrives, never speculatively.
 */

/** 2D Euclidean distance. */
export function distance(x1: number, y1: number, x2: number, y2: number): number {
  const dx = x2 - x1;
  const dy = y2 - y1;
  return Math.sqrt(dx * dx + dy * dy);
}

/** Random integer in `[min, max]` (inclusive both ends). */
export function between(min: number, max: number): number {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

/** Convert degrees → radians. */
export function degToRad(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/** Convert radians → degrees. */
export function radToDeg(radians: number): number {
  return (radians * 180) / Math.PI;
}

/** Clamp `n` into `[min, max]`. */
export function clamp(n: number, min: number, max: number): number {
  if (n < min) return min;
  if (n > max) return max;
  return n;
}

/** Linear interpolation `a → b` by `t` (t unclamped — extrapolates outside [0,1]). */
export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Clamp `n` into `[0, 1]`. */
export function clamp01(n: number): number {
  return n < 0 ? 0 : n > 1 ? 1 : n;
}

/** Channel-wise lerp between two 0xRRGGBB colours by `t` in [0,1] (t=0 → a, t=1 → b). */
export function lerpColor(a: number, b: number, t: number): number {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  const ar = (a >> 16) & 0xff, ag = (a >> 8) & 0xff, ab = a & 0xff;
  const br = (b >> 16) & 0xff, bg = (b >> 8) & 0xff, bb = b & 0xff;
  const r = Math.round(ar + (br - ar) * k);
  const g = Math.round(ag + (bg - ag) * k);
  const bl = Math.round(ab + (bb - ab) * k);
  return (r << 16) | (g << 8) | bl;
}
